import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Divider, Paper, Stack, Typography } from '@mui/material';

import type { MediaImage, PropertyRoomTypeWithImages } from '../types';
import type { RemovedRoomImageIdsByRoomKey, UploadedRoomAssignment } from '../utils';

import ImagesByRoomRow from './Row';
import {
  EMPTY_IMAGE_PUBLIC_IDS,
  applyRoomImageOrderOverrides,
  buildRoomImagesByKey,
  getRoomKey,
  pruneUnavailableRoomImages,
} from './utils';
import type { RoomImageOrderOverrides, RoomImagesByKey } from './utils';

const EMPTY_IMAGE_PUBLIC_ID_REPLACEMENTS: Record<string, string> = {};
const NOOP_TOGGLE_SELECTED = () => undefined;

interface Props {
  excludedImagePublicIds?: Array<string>;
  hiddenImagePublicIds?: Array<string>;
  imagePublicIdReplacements?: Record<string, string>;
  isMetadataEditable?: boolean;
  mediaImages: Array<MediaImage>;
  selectedImagePublicIds?: Array<string>;
  onEditImage: (publicId: string) => void;
  onImportRoomImages: (
    roomKey: string,
    photoUnits: Array<{ id_cloudinary_external: string; title?: string }>,
  ) => Promise<number>;
  onOpenImagesVideo?: () => void;
  onSyncTopRoomImages: (roomKey: string) => Promise<void>;
  onRemoveImageFromRoom: (image: MediaImage) => Promise<void> | void;
  onReorderRoomImages: (roomKey: string, orderedPublicIds: Array<string>) => Promise<void> | void;
  onToggleHiddenImage: (publicId: string) => Promise<void>;
  onToggleSelected?: (publicId: string) => void;
  onUpdateImageTitle?: (roomKey: string, publicId: string, title: string) => void;
  onUploadImagesToRoom?: (roomId: string, roomName: string, files: Array<File>) => void;
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
  removedRoomImageIdsByRoomKey?: RemovedRoomImageIdsByRoomKey;
  roomImageOrderOverrides: RoomImageOrderOverrides;
  roomImageOrderResetKey: number;
  showHeader?: boolean;
  showRoomDetails?: boolean;
  uploadedRoomAssignments?: Array<UploadedRoomAssignment>;
  visibleRoomKeys?: Array<string>;
}

function buildPropertyRoomTypesKey(propertyRoomTypes: Array<PropertyRoomTypeWithImages>) {
  return propertyRoomTypes
    .map((room, index) => {
      const imagePublicIds = (room.images ?? []).map((image) => image.id_cloudinary_external).join(',');

      return [getRoomKey(room, index), room.name, imagePublicIds].join(':');
    })
    .join('|');
}

export default function ImagesByRoom({
  excludedImagePublicIds = EMPTY_IMAGE_PUBLIC_IDS,
  hiddenImagePublicIds = EMPTY_IMAGE_PUBLIC_IDS,
  imagePublicIdReplacements = EMPTY_IMAGE_PUBLIC_ID_REPLACEMENTS,
  isMetadataEditable = false,
  mediaImages,
  selectedImagePublicIds = EMPTY_IMAGE_PUBLIC_IDS,
  onEditImage,
  onImportRoomImages,
  onOpenImagesVideo,
  onSyncTopRoomImages,
  onRemoveImageFromRoom,
  onReorderRoomImages,
  onToggleHiddenImage,
  onToggleSelected = NOOP_TOGGLE_SELECTED,
  onUpdateImageTitle,
  onUploadImagesToRoom,
  propertyRoomTypes = [],
  removedRoomImageIdsByRoomKey = {},
  roomImageOrderOverrides,
  roomImageOrderResetKey,
  showHeader = true,
  showRoomDetails = true,
  uploadedRoomAssignments = [],
  visibleRoomKeys,
}: Props) {
  const [roomImagesByKey, setRoomImagesByKey] = useState<RoomImagesByKey>(() =>
    buildInitialRoomImagesByKey(propertyRoomTypes, excludedImagePublicIds, removedRoomImageIdsByRoomKey, mediaImages),
  );
  const dismissedUploadedRoomAssignmentPublicIds = useRef<Set<string>>(new Set());
  const propertyRoomTypesKey = useMemo(() => buildPropertyRoomTypesKey(propertyRoomTypes), [propertyRoomTypes]);
  const previousPropertyRoomTypesKey = useRef(propertyRoomTypesKey);
  const previousRoomImageOrderResetKey = useRef(roomImageOrderResetKey);

  const dismissUploadedRoomAssignments = useCallback((publicIds: Array<string>) => {
    if (publicIds.length === 0) {
      return;
    }

    dismissedUploadedRoomAssignmentPublicIds.current = new Set([
      ...dismissedUploadedRoomAssignmentPublicIds.current,
      ...publicIds,
    ]);
  }, []);

  useEffect(() => {
    const hasRoomSourceChanged = previousPropertyRoomTypesKey.current !== propertyRoomTypesKey;

    if (hasRoomSourceChanged) {
      previousPropertyRoomTypesKey.current = propertyRoomTypesKey;
      dismissedUploadedRoomAssignmentPublicIds.current = new Set();
      setRoomImagesByKey(
        buildInitialRoomImagesByKey(
          propertyRoomTypes,
          excludedImagePublicIds,
          removedRoomImageIdsByRoomKey,
          mediaImages,
        ),
      );
      return;
    }

    setRoomImagesByKey((currentRoomImagesByKey) =>
      pruneUnavailableRoomImages(currentRoomImagesByKey, mediaImages, imagePublicIdReplacements),
    );
  }, [
    excludedImagePublicIds,
    imagePublicIdReplacements,
    mediaImages,
    propertyRoomTypes,
    propertyRoomTypesKey,
    removedRoomImageIdsByRoomKey,
  ]);

  useEffect(() => {
    if (uploadedRoomAssignments.length === 0) {
      return;
    }

    const activeUploadedRoomAssignments = uploadedRoomAssignments.filter(
      (assignment) => !dismissedUploadedRoomAssignmentPublicIds.current.has(assignment.publicId),
    );

    if (activeUploadedRoomAssignments.length === 0) {
      return;
    }

    const mediaImagesByPublicId = new Map(mediaImages.map((image) => [image.publicId, image]));

    setRoomImagesByKey((currentRoomImagesByKey) => {
      let nextRoomImagesByKey = currentRoomImagesByKey;
      const processedPublicIds: Array<string> = [];

      activeUploadedRoomAssignments.forEach((assignment) => {
        const mediaImage = mediaImagesByPublicId.get(assignment.publicId);

        if (!mediaImage) {
          return;
        }

        processedPublicIds.push(assignment.publicId);

        const currentRoomImages = nextRoomImagesByKey[assignment.roomId] ?? [];

        if (currentRoomImages.some((image) => image.publicId === assignment.publicId)) {
          return;
        }

        const selectedPublicIds = new Set([assignment.publicId]);
        const nextRoomImagesWithoutSelection = Object.fromEntries(
          Object.entries(nextRoomImagesByKey).map(([roomKey, roomImages]) => [
            roomKey,
            roomKey === assignment.roomId
              ? roomImages
              : roomImages.filter((image) => !selectedPublicIds.has(image.publicId)),
          ]),
        );

        nextRoomImagesByKey = {
          ...nextRoomImagesWithoutSelection,
          [assignment.roomId]: currentRoomImages.concat({
            publicId: mediaImage.publicId,
            title: mediaImage.title,
          }),
        };
      });

      dismissUploadedRoomAssignments(processedPublicIds);

      return nextRoomImagesByKey;
    });
  }, [dismissUploadedRoomAssignments, mediaImages, uploadedRoomAssignments]);

  useEffect(() => {
    const roomKeyById = new Set(propertyRoomTypes.map((room, index) => getRoomKey(room, index)));

    if (roomKeyById.size === 0) {
      return;
    }

    setRoomImagesByKey((currentRoomImagesByKey) =>
      syncRoomImagesWithMediaRooms(currentRoomImagesByKey, mediaImages, roomKeyById),
    );
  }, [mediaImages, propertyRoomTypes]);

  useEffect(() => {
    if (previousRoomImageOrderResetKey.current === roomImageOrderResetKey) {
      return;
    }

    previousRoomImageOrderResetKey.current = roomImageOrderResetKey;
    setRoomImagesByKey((currentRoomImagesByKey) => {
      const persistedRoomImagesByKey = buildRoomImagesByKey(
        propertyRoomTypes,
        excludedImagePublicIds,
        removedRoomImageIdsByRoomKey,
      );
      const availablePublicIds = new Set(mediaImages.map((image) => image.publicId));

      return Object.fromEntries(
        Object.entries(currentRoomImagesByKey).map(([roomKey, currentRoomImages]) => {
          const currentImagesByPublicId = new Map(currentRoomImages.map((image) => [image.publicId, image]));
          const persistedRoomImages = (persistedRoomImagesByKey[roomKey] ?? [])
            .map((image) => currentImagesByPublicId.get(image.publicId) ?? image)
            .filter((image) => availablePublicIds.has(image.publicId));
          const persistedImagePublicIds = new Set(persistedRoomImages.map((image) => image.publicId));
          const pendingRoomImages = currentRoomImages.filter(
            (image) => availablePublicIds.has(image.publicId) && !persistedImagePublicIds.has(image.publicId),
          );

          return [roomKey, persistedRoomImages.concat(pendingRoomImages)];
        }),
      );
    });
  }, [excludedImagePublicIds, mediaImages, propertyRoomTypes, removedRoomImageIdsByRoomKey, roomImageOrderResetKey]);

  useEffect(() => {
    setRoomImagesByKey((currentRoomImagesByKey) =>
      applyRoomImageOrderOverrides(currentRoomImagesByKey, roomImageOrderOverrides),
    );
  }, [roomImageOrderOverrides]);

  const handleRemoveImageFromRoom = (image: MediaImage) => {
    if (uploadedRoomAssignments.some((assignment) => assignment.publicId === image.publicId)) {
      dismissUploadedRoomAssignments([image.publicId]);
    }

    return onRemoveImageFromRoom(image);
  };

  if (propertyRoomTypes.length === 0) {
    return null;
  }

  const visibleRoomKeySet = visibleRoomKeys ? new Set(visibleRoomKeys) : undefined;
  const visibleRoomTypes = propertyRoomTypes
    .map((room, index) => ({ room, index }))
    .filter(({ room, index }) => !visibleRoomKeySet || visibleRoomKeySet.has(getRoomKey(room, index)));

  const roomRows = (
    <Stack divider={showRoomDetails ? <Divider /> : undefined} sx={{ px: showRoomDetails ? 3 : 0 }}>
      {visibleRoomTypes.map(({ room, index }) => {
        const roomKey = getRoomKey(room, index);

        return (
          <ImagesByRoomRow
            key={roomKey}
            hiddenImagePublicIds={hiddenImagePublicIds}
            isMetadataEditable={isMetadataEditable}
            mediaImages={mediaImages}
            selectedImagePublicIds={selectedImagePublicIds}
            onEditImage={onEditImage}
            onImportRoomImages={onImportRoomImages}
            onOpenImagesVideo={onOpenImagesVideo}
            onSyncTopRoomImages={onSyncTopRoomImages}
            onRemoveImageFromRoom={handleRemoveImageFromRoom}
            onReorderRoomImages={onReorderRoomImages}
            onToggleHiddenImage={onToggleHiddenImage}
            onToggleSelected={onToggleSelected}
            onUpdateImageTitle={onUpdateImageTitle}
            onUploadImagesToRoom={onUploadImagesToRoom}
            room={room}
            roomImages={roomImagesByKey[roomKey] ?? []}
            roomKey={roomKey}
            setRoomImagesByKey={setRoomImagesByKey}
            showRoomDetails={showRoomDetails}
          />
        );
      })}
    </Stack>
  );

  if (!showHeader) {
    return roomRows;
  }

  return (
    <Paper variant="outlined" sx={{ mt: 4, overflow: 'hidden' }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
        sx={{ px: 3, py: 2.5 }}
      >
        <Box>
          <Typography variant="h5">Images by room</Typography>
          <Typography variant="body2" color="text.secondary">
            Each row matches a room in{' '}
            <Box component="span" sx={{ fontWeight: 700 }}>
              Rooms
            </Box>
            . Add as many carousel images as needed.
          </Typography>
        </Box>
      </Stack>
      <Divider />
      {roomRows}
    </Paper>
  );
}

function syncRoomImagesWithMediaRooms(
  roomImagesByKey: RoomImagesByKey,
  mediaImages: Array<MediaImage>,
  roomKeyById: Set<string>,
) {
  let hasChanged = false;
  let nextRoomImagesByKey = roomImagesByKey;

  mediaImages.forEach((mediaImage) => {
    const targetRoomKey =
      mediaImage.sourceRoomId && roomKeyById.has(mediaImage.sourceRoomId) ? mediaImage.sourceRoomId : undefined;
    const existingRoomKey = Object.entries(nextRoomImagesByKey).find(([_roomKey, roomImages]) =>
      roomImages.some((roomImage) => roomImage.publicId === mediaImage.publicId),
    )?.[0];

    if (mediaImage.sourceType !== 'room' || !targetRoomKey) {
      if (!existingRoomKey) {
        return;
      }

      nextRoomImagesByKey = removeRoomImage(nextRoomImagesByKey, mediaImage.publicId);
      hasChanged = true;
      return;
    }

    const targetRoomImages = nextRoomImagesByKey[targetRoomKey] ?? [];
    const isAlreadyInTargetRoom = targetRoomImages.some((roomImage) => roomImage.publicId === mediaImage.publicId);

    const nextRoomImage = {
      publicId: mediaImage.publicId,
      title: mediaImage.title,
    };

    if (isAlreadyInTargetRoom) {
      const nextTargetRoomImages = targetRoomImages.map((roomImage) =>
        roomImage.publicId === mediaImage.publicId ? nextRoomImage : roomImage,
      );

      if (nextTargetRoomImages === targetRoomImages || areRoomImagesEqual(targetRoomImages, nextTargetRoomImages)) {
        return;
      }

      nextRoomImagesByKey = {
        ...nextRoomImagesByKey,
        [targetRoomKey]: nextTargetRoomImages,
      };
      hasChanged = true;
      return;
    }

    nextRoomImagesByKey = {
      ...removeRoomImage(nextRoomImagesByKey, mediaImage.publicId),
      [targetRoomKey]: targetRoomImages.concat(nextRoomImage),
    };
    hasChanged = true;
  });

  return hasChanged ? nextRoomImagesByKey : roomImagesByKey;
}

function removeRoomImage(roomImagesByKey: RoomImagesByKey, publicId: string) {
  return Object.fromEntries(
    Object.entries(roomImagesByKey).map(([roomKey, roomImages]) => [
      roomKey,
      roomImages.filter((roomImage) => roomImage.publicId !== publicId),
    ]),
  );
}

function areRoomImagesEqual(
  firstRoomImages: Array<{ publicId: string; title?: string }>,
  secondRoomImages: Array<{ publicId: string; title?: string }>,
) {
  return (
    firstRoomImages.length === secondRoomImages.length &&
    firstRoomImages.every(
      (firstRoomImage, index) =>
        firstRoomImage.publicId === secondRoomImages[index].publicId &&
        firstRoomImage.title === secondRoomImages[index].title,
    )
  );
}

function buildInitialRoomImagesByKey(
  propertyRoomTypes: Array<PropertyRoomTypeWithImages>,
  excludedImagePublicIds: Array<string>,
  removedRoomImageIdsByRoomKey: RemovedRoomImageIdsByRoomKey,
  mediaImages: Array<MediaImage>,
) {
  return pruneUnavailableRoomImages(
    buildRoomImagesByKey(propertyRoomTypes, excludedImagePublicIds, removedRoomImageIdsByRoomKey),
    mediaImages,
  );
}
